import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, ArrowRight, Clock } from 'lucide-react';
import api from './api';
import { getCurrentLanguage } from '../utils/translations';

const EvenementSection = ({ limit = 6, showTitle = true }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming, all, featured
  const currentLanguage = getCurrentLanguage();

  useEffect(() => {
    fetchEvents();
  }, [filter]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/api/events/', {
        params: { filter }
      });
      setEvents(response.data.slice(0, limit));
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    };
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'fr-FR', options);
  };

  const isEventDateValid = (dateString) => {
    const eventDate = new Date(dateString);
    const now = new Date();
    // Comparer seulement les dates (sans l'heure)
    eventDate.setHours(23, 59, 59, 999);
    return eventDate >= now;
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'fr-FR', { month: 'short' })
    };
  };

  if (loading) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mx-auto mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 bg-gray-300 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Hide section if no upcoming events
  if (events.length === 0 && filter === 'upcoming') {
    return null;
  }

  const content = {
    fr: {
      title: 'Événements à Venir',
      participate: 'Je Participe',
      location: 'Lieu',
      date: 'Date',
      featured: 'En vedette',
      upcoming: 'À venir',
      all: 'Tous',
      noEvents: 'Aucun événement disponible'
    },
    en: {
      title: 'Upcoming Events',
      participate: 'Register',
      location: 'Location',
      date: 'Date',
      featured: 'Featured',
      upcoming: 'Upcoming',
      all: 'All',
      noEvents: 'No events available'
    }
  };

  const t = (key) => content[currentLanguage === 'en' ? 'en' : 'fr'][key];

  return (
    <section className="py-12 bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* En-tête avec filtres */}
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              {t('title')}
            </h2>
            
            {/* Filtres */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => setFilter('upcoming')}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  filter === 'upcoming'
                    ? 'bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {t('upcoming')}
              </button>
              <button
                onClick={() => setFilter('featured')}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  filter === 'featured'
                    ? 'bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {t('featured')}
              </button>
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  filter === 'all'
                    ? 'bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {t('all')}
              </button>
            </div>
          </div>
        )}

        {/* Grille d'événements */}
        {events.length > 0 ? (
          <div className={events.length === 1 ? "" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"}>
            {events.map((event) => {
              const title = currentLanguage === 'en' ? event.title_en : event.title_fr;
              const description = currentLanguage === 'en' ? event.description_en : event.description_fr;
              const dateInfo = formatDateShort(event.event_date);
              const isDateValid = isEventDateValid(event.event_date);

              // Layout horizontal pour un seul événement
              if (events.length === 1) {
                return (
                  <div
                    key={event.id}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Image avec badge date - S'adapte à la hauteur du contenu */}
                      <div className="relative w-full md:w-1/2 md:min-h-full overflow-hidden">
                        <img
                          src={event.image_url}
                          alt={title}
                          className="w-full h-full min-h-[300px] md:min-h-0 object-cover object-center group-hover:scale-110 transition-transform duration-300"
                          style={{ imageRendering: '-webkit-optimize-contrast' }}
                          loading="lazy"
                        />
                        
                        {/* Badge date */}
                        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 text-center min-w-[70px]">
                          <div className="text-3xl font-bold text-[#089556]">{dateInfo.day}</div>
                          <div className="text-sm font-medium text-gray-600 uppercase">{dateInfo.month}</div>
                        </div>

                        {/* Badge featured */}
                        {event.is_featured && (
                          <div className="absolute top-4 right-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white px-3 py-1 rounded-full text-xs font-semibold">
                            {t('featured')}
                          </div>
                        )}
                      </div>

                      {/* Contenu */}
                      <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3 group-hover:text-[#089556] transition-colors">
                          {title}
                        </h3>
                        
                        <div 
                          className="text-gray-600 mb-4 text-base md:text-lg"
                          dangerouslySetInnerHTML={{ __html: description }}
                        />

                        {/* Informations */}
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-base text-gray-600">
                            <Calendar className="w-5 h-5 mr-3 text-[#089556]" />
                            <span>{formatDate(event.event_date)}</span>
                          </div>
                        </div>

                        {/* Bouton d'action */}
                        {event.registration_link ? (
                          <a
                            href={event.registration_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                          >
                            {t('participate')}
                            <ArrowRight className="w-5 h-5 ml-2" />
                          </a>
                        ) : (
                          <button
                            className="inline-flex items-center justify-center px-8 py-4 bg-gray-100 text-gray-400 rounded-lg font-semibold cursor-not-allowed"
                            disabled
                          >
                            {t('participate')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // Layout grille pour plusieurs événements
              return (
                <div
                  key={event.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  {/* Image avec badge date - Hauteur proportionnelle */}
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={title}
                      className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
                      style={{ imageRendering: '-webkit-optimize-contrast' }}
                      loading="lazy"
                    />
                    
                    {/* Badge date */}
                    <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 text-center min-w-[70px]">
                      <div className="text-3xl font-bold text-[#089556]">{dateInfo.day}</div>
                      <div className="text-sm font-medium text-gray-600 uppercase">{dateInfo.month}</div>
                    </div>

                    {/* Badge featured */}
                    {event.is_featured && (
                      <div className="absolute top-4 right-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {t('featured')}
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#089556] transition-colors">
                      {title}
                    </h3>
                    
                    <div 
                      className="text-gray-600 mb-4"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />

                    {/* Informations */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="w-4 h-4 mr-2 text-[#089556]" />
                        <span>{formatDate(event.event_date)}</span>
                      </div>
                    </div>

                    {/* Bouton d'action */}
                    {event.registration_link ? (
                      <a
                        href={event.registration_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                      >
                        {t('participate')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </a>
                    ) : (
                      <button
                        className="inline-flex items-center justify-center w-full px-6 py-3 bg-gray-100 text-gray-400 rounded-lg font-semibold cursor-not-allowed"
                        disabled
                      >
                        {t('participate')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Message si aucun événement - Toujours afficher quelque chose
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500 mb-2">{t('noEvents')}</p>
            <p className="text-gray-400">
              {filter === 'featured' && 'Aucun événement en vedette pour le moment'}
              {filter === 'upcoming' && 'Aucun événement à venir'}
              {filter === 'all' && 'Aucun événement disponible'}
            </p>
            <button
              onClick={() => setFilter('all')}
              className="mt-4 px-6 py-2 bg-[#089556] text-white rounded-full hover:bg-[#078a4a] transition-colors"
            >
              Voir tous les événements
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default EvenementSection;